import React, { Component } from 'react'
import styled, { keyframes } from 'react-emotion'
import { injectGlobal } from 'emotion'
import netlifyIdentity from 'netlify-identity-widget'

injectGlobal({
  '*': {
    boxSizing: 'border-box',
    margin: '0',
    padding: '0',
  }
})

const fadeUp = keyframes({
  '0%': {
    opacity: 0,
    transform: 'translateY(4rem)',
  },
  '100%': {
    opacity: 1,
    transform: 'translateY(0rem)',
  },
})

const Container = styled('div')(() => ({
  backgroundImage: 'linear-gradient(to right top, #9d00ff, #be2dfa, #d84bf7, #ee67f5, #ff82f5)',
  minHeight: '100vh',
  padding: '4rem 2rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}))

const Title = styled('h1')(() => ({
  opacity: 0,
  animation: `${fadeUp} 1s ease-out forwards`,
  fontSize: '4rem',
  letterSpacing: '-0.1em',
  lineHeight: '1.5',
  color: 'white',
  fontWeight: 'bold',
  textAlign: 'center',
  margin: '0',
  textShadow: '0.05em 0.05em 0 #9d00ff',
  '@media (min-width: 512px)': {
    fontSize: '6rem',
  },
  '@media (min-width: 1024px)': {
    fontSize: '8rem',
  }
}))

const Subtitle = styled('h3')(() => ({
  opacity: 0,
  animation: `${fadeUp} 1s ease-out forwards`,
  animationDelay: '250ms',
  fontSize: '1.5rem',
  fontWeight: 100,
  letterSpacing: '-0.05em',
  textShadow: '0.05em 0.05em 0 #9d00ff',
  lineHeight: '1.5',
  color: 'white',
  textAlign: 'center',
  fontStyle: 'italic',
  margin: 0,
  '@media (min-width: 512px)': {
    fontSize: '2rem',
  },
  '@media (min-width: 1024px)': {
    fontSize: '2.5rem',
  }
}))

const Form = styled('form')(() => ({
  margin: '4rem auto',
  position: 'relative',
  maxWidth: '40rem',
  '> *': {
    opacity: 0,
    animation: `${fadeUp} 1s ease-out forwards`,
  }
}))

const Field = styled('div')(({ isOptional, hasError }) => ({
  animationDelay: '500ms',
  position: 'relative',
  '&:not(:last-child)': {
    marginBottom: '1.5rem',
  },
  '&:after': {
    content: hasError
      ? '"that slug is already taken"'
      : isOptional
          ? '"only if you want"'
          : null,
    color: hasError
      ? '#ff6961'
      : '#9d00ff',
    backgroundColor: 'white',
    padding: '0 0.25rem',
    borderTopLeftRadius: '5px',
    borderBottomRightRadius: '5px',
    fontSize: '0.75em',
    lineHeight: '1.25',
    opacity: 0.75,
    position: 'absolute',
    right: 0,
    bottom: 0,
  }
}))

const Input = styled('input')(() => ({
  width: '100%',
  color: 'white',
  border: 0,
  background: 'rgba(255,255,255,0.35)',
  padding: '0 1rem',
  lineHeight: 2.25,
  fontSize: '2rem',
  borderRadius: '5px',
  outlineColor: 'white',
  '::placeholder': {
    textAlign: 'center',
    color: 'white',
    opacity: '0.75',
  },
}))

const SlugContainer = styled('div')(() => ({
  opacity: 0,
  animation: `${fadeUp} 1s ease-out forwards`,
  animationDelay: '750ms',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '1.5rem',
}))

const SlugTitle = styled('p')(() => ({
  color: 'white',
  fontSize: '1rem',
  fontWeight: '100',
  marginRight: '0.5rem',
  '@media (min-width: 512px)': {
    fontSize: '2rem',
  }
}))

const Submit = styled('button')(() => ({
  opacity: 0,
  animation: `${fadeUp} 1s ease-out forwards`,
  animationDelay: '1000ms',
  width: '100%',
  background: 'rgba(255,255,255,0.8)',
  boxShadow: '0 0.2em 0 #9d00ff',
  border: 0,
  fontSize: '2rem',
  lineHeight: 2.25,
  color: '#9d00ff',
  padding: '0 2rem',
  cursor: 'pointer',
  borderRadius: '5px',
  transition: '75ms ease-out',
  '&:focus, &:hover': {
    background: 'rgba(255,255,255,0.9)',
  },
  '&:active': {
    background: 'rgba(255,255,255,1)',
    transform: 'translateY(0.15em)',
    boxShadow: '0 0.05em 0 #9d00ff',
  }
}))

const SuccessMessage = styled('div')(() => ({
  background: '#77dd77',
  position: 'absolute',
  left: '-5%',
  top: '-5%',
  width: '110%',
  minHeight: '110%',
  padding: '2rem',
  borderRadius: '5px',
}))

const SuccessTitle = styled('h1')(() => ({
  fontSize: '2rem',
  letterSpacing: '-0.1em',
  lineHeight: '1.5',
  color: 'white',
  fontWeight: 'bold',
  textAlign: 'center',
  margin: '0',
  textShadow: '0.05em 0.05em 0 #3ace3a',
  marginBottom: '1rem',
  '@media (min-width: 512px)': {
    fontSize: '2.5rem',
  },
  '@media (min-width: 1024px)': {
    fontSize: '3rem',
  },
}))

const SuccessButton = styled('button')(() => ({
  width: '100%',
  background: 'rgba(255,255,255,0.8)',
  boxShadow: '0 0.2em 0 #3ace3a',
  border: 0,
  fontSize: '2rem',
  lineHeight: 2.25,
  color: '#3ace3a',
  padding: '0 2rem',
  cursor: 'pointer',
  borderRadius: '5px',
  transition: '75ms ease-out',
  '&:focus, &:hover': {
    background: 'rgba(255,255,255,0.9)',
  },
  '&:active': {
    background: 'rgba(255,255,255,1)',
    transform: 'translateY(0.15em)',
    boxShadow: '0 0.05em 0 #3ace3a',
  }
}))

class ResultMessage extends Component {
  componentDidMount() {
    if (this.input) {
      this.input.focus()
      this.input.select()
    }
  }

  render() {
    const { message, onClose } = this.props

    return (
      <SuccessMessage>
        <SuccessTitle><em>Nice!</em> Now it's super chiquito.</SuccessTitle>
        <Field>
          <Input defaultValue={message} innerRef={(input) => { this.input = input }} />
        </Field>
        <SuccessButton type="button" onClick={onClose}>Uno Más</SuccessButton>
      </SuccessMessage>
    )
  }
}

class Shortener extends Component {
  constructor() {
    super()

    this.state = {
      form: {
        url: '',
        slug: '',
      },
      hasPermission: true,
      result: '',
      isLoading: false,
    }

    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.restart = this.restart.bind(this)
  }

  restart() {
    this.setState({
      form: {
        url: '',
        slug: '',
      },
      hasPermission: true,
      result: '',
      isLoading: false,
    })
  }

  handleInputChange({ target }) {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        [target.name]: target.value,
      },
    }))
  }

  handleFormSubmit(e) {
    e.preventDefault()
    this.setState({
      isLoading: true,
    }, () => {
      fetch('/.netlify/functions/generateShortUrl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(this.state.form),
      })
        .then((response) => {
          if (response.ok) {
            response.json()
              .then(result => {
                this.setState({
                  isLoading: false,
                  hasError: false,
                  result: `${window.location.protocol}//${window.location.host}/${result.slug}`,
                  form: {
                    url: '',
                    slug: '',
                  }
                })
              })
          } else {
            this.setState(({ form }) => ({
              isLoading: false,
              hasError: true,
              form: {
                ...form,
                slug: '',
              }
            }))
          }


        })
        .catch((e, r) => console.error('ERROR', e, r))
    })
  }

  render() {
    const { form, isLoading, hasPermission, hasError } = this.state

    if (!hasPermission) {
      return false
    }

    return (
      <Container>
        <Title>URL Chiquito</Title>
        <Subtitle>¡Make long URLs chiquito!</Subtitle>
        <Form onSubmit={this.handleFormSubmit}>
          <Field>
            <Input
              name="url"
              placeholder="URL"
              type="url"
              required
              value={form.url}
              onChange={this.handleInputChange}
              autoFocus
            />
          </Field>
          <SlugContainer>
            <SlugTitle>https://sau.pe/</SlugTitle>
            <Field isOptional hasError={hasError}>
              <Input
                name="slug"
                placeholder="slug"
                type="text"
                value={form.slug}
                onChange={this.handleInputChange}
              />
            </Field>
          </SlugContainer>

          <Submit
            type="submit"
          >
            ¡Make it Chiquito!
          </Submit>
          {this.state.result
            ? <ResultMessage message ={this.state.result} onClose={this.restart} />
            : null
          }
        </Form>

      </Container>
    )
  }
}

export default Shortener



// WEBPACK FOOTER //
// ./src/pages/Shortener.jsx